import React from 'react';
import { Link } from 'react-router-dom';
import { mockLaunches, mockAgencies, mockNews } from '../services/dataService';
import CountdownTimer from '../components/CountdownTimer';
import { getUpcomingLaunches } from '../services/dataService';
import { getAgencyImage } from '../utils/imageUtils';

/**
 * Home Page Component
 * Hero section, upcoming launches, featured agencies, and latest news
 */
const Home = () => {
  const upcomingLaunches = getUpcomingLaunches().slice(0, 3);
  const featuredAgencies = mockAgencies.slice(0, 4);
  const latestNews = mockNews.slice(0, 3);

  return (
    <div>
      {/* Hero Section */}
      <section style={{ 
        background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%)',
        padding: '100px 20px',
        textAlign: 'center',
        marginBottom: '50px'
      }}>
        <div className="container">
          <h1 style={{ fontSize: '48px', marginBottom: '20px', color: 'var(--highlight-color)' }}>
            🚀 Welcome to RocketVerse
          </h1>
          <p style={{ fontSize: '24px', marginBottom: '30px', color: 'var(--text-dark)' }}>
            Explore the Universe, One Launch at a Time
          </p>
          <p style={{ fontSize: '18px', marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px' }}>
            Discover space agencies, rockets, launches, scientists, satellites, and the latest in space exploration news.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
            <Link to="/register" className="btn btn-primary">Get Started</Link>
            <Link to="/launches" className="btn btn-secondary">View Launches</Link>
          </div>
        </div>
      </section>

      <div className="container">
        {/* Upcoming Launches Section */}
        <section style={{ marginBottom: '50px' }}>
          <h2 style={{ fontSize: '32px', marginBottom: '30px', textAlign: 'center' }}>
            🔥 Upcoming Launches
          </h2>
          {upcomingLaunches.length > 0 ? (
            <div className="grid">
              {upcomingLaunches.map(launch => (
                <div key={launch.id} className="card">
                  <h3 style={{ color: 'var(--highlight-color)', marginBottom: '15px' }}>
                    {launch.missionName}
                  </h3>
                  <p style={{ marginBottom: '10px' }}>
                    <strong>Date:</strong> {new Date(launch.launchDate).toLocaleDateString()}
                  </p>
                  <p style={{ marginBottom: '10px' }}>
                    <strong>Site:</strong> {launch.launchSite}
                  </p>
                  <p style={{ marginBottom: '15px' }}>
                    <strong>Type:</strong> {launch.missionType}
                  </p>
                  <CountdownTimer targetDate={launch.launchDate} />
                  <Link to={`/launches/${launch.id}`} className="btn btn-primary" style={{ marginTop: '15px', display: 'block', textAlign: 'center' }}>
                    View Details
                  </Link>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: 'var(--text-dark)' }}>No upcoming launches scheduled.</p>
          )}
        </section>

        {/* Featured Agencies Section */}
        <section style={{ marginBottom: '50px' }}>
          <h2 style={{ fontSize: '32px', marginBottom: '30px', textAlign: 'center' }}>
            🌍 Featured Space Agencies
          </h2>
          <div className="grid">
            {featuredAgencies.map(agency => (
              <div key={agency.id} className="card">
                <img 
                  src={getAgencyImage(agency.id, agency.name)} 
                  alt={agency.name}
                  style={{ 
                    width: '100%', 
                    height: '180px', 
                    objectFit: 'cover', 
                    borderRadius: '8px', 
                    marginBottom: '15px' 
                  }}
                  onError={(e) => {
                    e.target.src = `https://via.placeholder.com/400x300/1a1a2e/e94560?text=${encodeURIComponent(agency.name)}`;
                  }}
                />
                <h3 style={{ color: 'var(--highlight-color)', marginBottom: '15px' }}>
                  {agency.name}
                </h3>
                <p style={{ marginBottom: '10px' }}>
                  <strong>Country:</strong> {agency.country}
                </p>
                <p style={{ marginBottom: '10px' }}>
                  <strong>Founded:</strong> {agency.foundedYear}
                </p>
                <p style={{ marginBottom: '15px', color: 'var(--text-dark)' }}>
                  {agency.description.substring(0, 100)}...
                </p>
                <Link to={`/agencies/${agency.id}`} className="btn btn-primary" style={{ display: 'block', textAlign: 'center' }}>
                  Learn More
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Latest News Section */}
        <section style={{ marginBottom: '50px' }}>
          <h2 style={{ fontSize: '32px', marginBottom: '30px', textAlign: 'center' }}>
            📰 Latest News
          </h2>
          <div className="grid">
            {latestNews.map(news => (
              <div key={news.id} className="card">
                <h3 style={{ color: 'var(--highlight-color)', marginBottom: '15px' }}>
                  {news.headline}
                </h3>
                <p style={{ marginBottom: '10px', color: 'var(--text-dark)' }}>
                  {news.summary}
                </p>
                <p style={{ marginBottom: '15px', fontSize: '14px' }}>
                  <strong>Source:</strong> {news.source} | <strong>Date:</strong> {news.date}
                </p>
                <Link to={`/news/${news.id}`} className="btn btn-secondary" style={{ display: 'block', textAlign: 'center' }}>
                  Read More
                </Link>
              </div>
            ))}
          </div>
        </section>

        {/* Call to Action Section */}
        <section style={{ 
          background: 'var(--card-bg)',
          padding: '50px 20px',
          borderRadius: '10px',
          textAlign: 'center',
          marginBottom: '50px'
        }}>
          <h2 style={{ fontSize: '32px', marginBottom: '20px' }}>
            Ready to Explore?
          </h2>
          <p style={{ fontSize: '18px', marginBottom: '30px', color: 'var(--text-dark)' }}>
            Join RocketVerse to get personalized launch updates, access premium features, and connect with space enthusiasts.
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
            <Link to="/register" className="btn btn-primary">Register Now</Link>
            <Link to="/subscription" className="btn btn-secondary">View Plans</Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Home;
